import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { PageHeader, Card, Modal } from '../components/ui'

const CATEGORY_LABEL: Record<string, string> = {
  APPETIZER: 'Entradas',
  MAIN: 'Platos fuertes',
  DESSERT: 'Postres',
  DRINK: 'Bebidas',
  SIDE: 'Guarniciones',
}

interface EventItem {
  id: string
  name: string
  startedAt: string | null
  endedAt: string | null
  active: boolean
  createdAt: string
  _count: { orders: number; tables: number }
}

interface EventsResponse {
  events: EventItem[]
  eventCredits: number
}

interface ReportRow {
  dishId: string
  dishName: string
  category: string
  eventStock: number | null
  totalQty: number
  totalAmount: number
}

interface EventReport {
  event: EventItem
  rows: ReportRow[]
  grandQty: number
  grandTotal: number
  tablesWithServed: number
}

interface TableItem {
  id: string
  number: number
  type: string
}

export default function EventPage() {
  const qc = useQueryClient()
  const [newEventName, setNewEventName] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [tableAssignEvent, setTableAssignEvent] = useState<EventItem | null>(null)
  const [selectedTableIds, setSelectedTableIds] = useState<string[]>([])
  const [tablesInitialized, setTablesInitialized] = useState(false)

  const { data, isLoading } = useQuery<EventsResponse>({
    queryKey: ['events'],
    queryFn: async () => (await api.get('/events')).data,
  })

  const { data: report, isLoading: reportLoading } = useQuery<EventReport>({
    queryKey: ['event-report', selectedEventId],
    queryFn: async () => (await api.get(`/events/${selectedEventId}/report`)).data,
    enabled: !!selectedEventId,
  })

  const { data: allTables = [] } = useQuery<TableItem[]>({
    queryKey: ['tables'],
    queryFn: async () => (await api.get('/tables')).data,
    enabled: !!tableAssignEvent,
  })

  const { data: assignedTableIds } = useQuery<string[]>({
    queryKey: ['event-tables', tableAssignEvent?.id],
    queryFn: async () => (await api.get(`/events/${tableAssignEvent!.id}/tables`)).data,
    enabled: !!tableAssignEvent,
  })

  useEffect(() => {
    if (assignedTableIds && !tablesInitialized) {
      setSelectedTableIds(assignedTableIds)
      setTablesInitialized(true)
    }
  }, [assignedTableIds, tablesInitialized])

  const createMutation = useMutation({
    mutationFn: (name: string) => api.post('/events', { name }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['events'] }); setNewEventName(''); setShowCreate(false) },
    onError: (e: any) => alert(e.response?.data?.message ?? 'Error al crear evento'),
  })

  const activateMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/events/${id}/activate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
    onError: (e: any) => alert(e.response?.data?.message ?? 'Error al activar evento'),
  })

  const closeMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/events/${id}/close`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
    onError: (e: any) => alert(e.response?.data?.message ?? 'Error al cerrar evento'),
  })

  const saveTablesMutation = useMutation({
    mutationFn: ({ eventId, tableIds }: { eventId: string; tableIds: string[] }) =>
      api.put(`/events/${eventId}/tables`, { tableIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] })
      qc.invalidateQueries({ queryKey: ['event-tables', tableAssignEvent?.id] })
      setTableAssignEvent(null)
    },
    onError: (e: any) => alert(e.response?.data?.message ?? 'Error al guardar mesas'),
  })

  function openTableAssign(event: EventItem) {
    setTableAssignEvent(event)
    setSelectedTableIds([])
    setTablesInitialized(false)
  }

  const events = data?.events ?? []
  const credits = data?.eventCredits ?? 0
  const TABLE_TYPE_LABEL: Record<string, string> = {
    DINE_IN: 'Comedor',
    TAKEOUT: 'Para llevar',
    ROOM: 'Habitación',
    COUNTER: 'Barra',
  }

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Eventos"
        subtitle="Gestión de eventos y consumo por platillo"
      />

      {/* Créditos + crear */}
      <Card className="p-5 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Créditos disponibles</p>
          <p className={`text-3xl font-black ${credits === 0 ? 'text-red-500' : 'text-gray-900'}`}>{credits}</p>
          {credits === 0 && (
            <p className="text-xs text-red-400 mt-1">Sin créditos — contacta a TEFFY para recargar</p>
          )}
        </div>
        <button
          onClick={() => setShowCreate((v) => !v)}
          disabled={credits === 0}
          className="bg-gray-900 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
        >
          + Nuevo evento
        </button>
      </Card>

      {showCreate && (
        <Card className="p-5 space-y-3">
          <p className="text-sm font-semibold text-gray-700">Nombre del evento</p>
          <input
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
            placeholder="Ej. Boda García, Cena de empresa dic 2026…"
            value={newEventName}
            onChange={(e) => setNewEventName(e.target.value)}
            autoFocus
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreate(false)} className="text-sm text-gray-500 px-3 py-2 hover:text-gray-800">
              Cancelar
            </button>
            <button
              onClick={() => { if (newEventName.trim()) createMutation.mutate(newEventName.trim()) }}
              disabled={!newEventName.trim() || createMutation.isPending}
              className="bg-gray-900 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-40"
            >
              {createMutation.isPending ? 'Creando…' : 'Crear'}
            </button>
          </div>
        </Card>
      )}

      {/* Lista de eventos */}
      {isLoading ? (
        <div className="text-sm text-gray-400">Cargando…</div>
      ) : events.length === 0 ? (
        <Card className="py-16 text-center">
          <p className="text-4xl mb-4">🎉</p>
          <p className="font-semibold text-gray-700">Sin eventos creados</p>
          <p className="text-sm text-gray-400 mt-1">Crea un evento para comenzar a controlar el stock y consumo</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <Card key={event.id} className={`p-5 ${event.active ? 'ring-2 ring-green-400' : ''}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-900 text-sm">{event.name}</span>
                    {event.active && (
                      <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                        En curso
                      </span>
                    )}
                    {event.endedAt && (
                      <span className="bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full">
                        Cerrado
                      </span>
                    )}
                    {!event.active && !event.endedAt && (
                      <span className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                        Sin iniciar
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-1 space-x-3">
                    {event.startedAt && <span>Inicio: {new Date(event.startedAt).toLocaleString('es-MX')}</span>}
                    {event.endedAt && <span>Cierre: {new Date(event.endedAt).toLocaleString('es-MX')}</span>}
                    <span>{event._count.orders} órdenes</span>
                    {event._count.tables > 0 && (
                      <span className="text-blue-500 font-medium">{event._count.tables} mesa(s) asignada(s)</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                  {event._count.orders > 0 && (
                    <button
                      onClick={() => setSelectedEventId(selectedEventId === event.id ? null : event.id)}
                      className="text-xs text-gray-500 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50"
                    >
                      {selectedEventId === event.id ? 'Ocultar reporte' : 'Ver reporte'}
                    </button>
                  )}
                  {!event.endedAt && (
                    <button
                      onClick={() => openTableAssign(event)}
                      className="text-xs text-blue-600 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-50"
                    >
                      Mesas
                    </button>
                  )}
                  {!event.active && !event.endedAt && (
                    <button
                      onClick={() => { if (confirm('¿Activar este evento? Habilitará el modo evento en el menú.')) activateMutation.mutate(event.id) }}
                      disabled={activateMutation.isPending}
                      className="text-xs bg-green-600 text-white font-semibold rounded-lg px-3 py-1.5 disabled:opacity-40 hover:bg-green-700"
                    >
                      Activar
                    </button>
                  )}
                  {event.active && (
                    <button
                      onClick={() => { if (confirm('¿Cerrar el evento? Deshabilitará el modo evento en el menú.')) closeMutation.mutate(event.id) }}
                      disabled={closeMutation.isPending}
                      className="text-xs bg-red-600 text-white font-semibold rounded-lg px-3 py-1.5 disabled:opacity-40 hover:bg-red-700"
                    >
                      Cerrar evento
                    </button>
                  )}
                </div>
              </div>

              {/* Reporte inline */}
              {selectedEventId === event.id && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                  {reportLoading ? (
                    <p className="text-sm text-gray-400">Cargando reporte…</p>
                  ) : !report || report.rows.length === 0 ? (
                    <p className="text-sm text-gray-400">Sin consumo registrado aún</p>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">Porciones</p>
                          <p className="text-xl font-black text-gray-900">{report.grandQty}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">Total</p>
                          <p className="text-xl font-black text-gray-900">${report.grandTotal.toFixed(2)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">Órdenes servidas</p>
                          <p className="text-xl font-black text-gray-900">{report.tablesWithServed}</p>
                        </div>
                      </div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide">Platillo</th>
                            <th className="text-right py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-24">Restante</th>
                            <th className="text-right py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-20">Servido</th>
                            <th className="text-right py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-28">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {report.rows.map((row) => {
                            const restante = row.eventStock != null ? Math.max(0, row.eventStock - row.totalQty) : null
                            return (
                            <tr key={row.dishId}>
                              <td className="py-2 text-gray-900">{row.dishName}</td>
                              <td className="py-2 text-right">
                                {restante == null ? (
                                  <span className="text-gray-400">—</span>
                                ) : restante === 0 ? (
                                  <span className="text-red-500 font-semibold">Agotado</span>
                                ) : (
                                  <span className={restante <= 5 ? 'text-amber-600 font-semibold' : 'text-gray-700'}>{restante}</span>
                                )}
                              </td>
                              <td className="py-2 text-right font-semibold text-gray-900">{row.totalQty}</td>
                              <td className="py-2 text-right text-gray-700">${row.totalAmount.toFixed(2)}</td>
                            </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Modal asignación de mesas */}
      <Modal
        title={`Mesas — ${tableAssignEvent?.name}`}
        open={!!tableAssignEvent}
        onClose={() => setTableAssignEvent(null)}
      >
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Selecciona las mesas que participan en este evento. Solo esas mesas verán el menú en modo evento.
            Si no asignas ninguna, el modo evento aplica a todas las mesas del restaurante.
          </p>

          {allTables.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No hay mesas registradas</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{selectedTableIds.length} de {allTables.length} seleccionada(s)</span>
                <button
                  type="button"
                  className="text-xs text-blue-500 hover:text-blue-700"
                  onClick={() => {
                    if (selectedTableIds.length === allTables.length) {
                      setSelectedTableIds([])
                    } else {
                      setSelectedTableIds(allTables.map((t) => t.id))
                    }
                  }}
                >
                  {selectedTableIds.length === allTables.length ? 'Ninguna' : 'Todas'}
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
                {allTables
                  .slice()
                  .sort((a, b) => a.number - b.number)
                  .map((table) => (
                    <label key={table.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTableIds.includes(table.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTableIds((prev) => [...prev, table.id])
                          } else {
                            setSelectedTableIds((prev) => prev.filter((id) => id !== table.id))
                          }
                        }}
                        className="accent-blue-500 w-4 h-4 shrink-0"
                      />
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-sm font-semibold text-gray-900">Mesa {table.number}</span>
                        <span className="text-xs text-gray-400">{TABLE_TYPE_LABEL[table.type] ?? table.type}</span>
                      </div>
                    </label>
                  ))}
              </div>
              {selectedTableIds.length === 0 && (
                <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                  Sin mesas seleccionadas — el modo evento aplicará a todas las mesas del restaurante
                </p>
              )}
            </>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => setTableAssignEvent(null)}
              className="text-sm text-gray-500 px-4 py-2 hover:text-gray-800"
            >
              Cancelar
            </button>
            <button
              onClick={() => saveTablesMutation.mutate({ eventId: tableAssignEvent!.id, tableIds: selectedTableIds })}
              disabled={saveTablesMutation.isPending}
              className="bg-gray-900 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-40 hover:bg-gray-700"
            >
              {saveTablesMutation.isPending ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
